/* eslint-disable no-unused-expressions */
import { getLogger } from '@jitsi/logger';
import $ from 'jquery';
import * as JitsiConferenceEvents from 'lib-jitsi-meet/dist/esm/JitsiConferenceEvents';
import * as JitsiTranscriptionStatus from 'lib-jitsi-meet/dist/esm/JitsiTranscriptionStatus';
import { XMPPEvents } from 'lib-jitsi-meet/dist/esm/service/xmpp/XMPPEvents';
import { isEqual } from 'lodash-es';
import { v4 as uuidv4 } from 'uuid';

import { kickUserIfSameJidAndDifferentResource } from '../../native/functions';
import WildixEvents from '../../service/WildixEvents';
import WildixXMPPEvents from '../../service/xmpp/WildixXMPPEvents';

const logger = getLogger('ChatRoomOverride');

const FEATURE_TRANSCRIBER = 'http://jitsi.org/protocol/transcriber';

export const parser = {
    packet2JSON(xmlElement, nodes) {
        for (const child of Array.from(xmlElement.children)) {
            const node = {
                attributes: {},
                children: [],
                tagName: child.tagName
            };
            for (const attr of Array.from(child.attributes)) {
                node.attributes[attr.name] = attr.value;
            }
            const text = Strophe.getText(child);
            if (text) {
                // Using Strophe.getText will do work for traversing all direct
                // child text nodes but returns an escaped value, which is not
                // desirable at this point.
                node.value = Strophe.xmlunescape(text);
            }
            nodes.push(node);
            this.packet2JSON(child, node.children);
        }
    },
    json2packet(nodes, packet) {
        for (let i = 0; i < nodes.length; i++) {
            const node = nodes[i];
            if (node) {
                packet.c(node.tagName, node.attributes);
                if (node.value) {
                    packet.t(node.value);
                }
                if (node.children) {
                    this.json2packet(node.children, packet);
                }
                packet.up();
            }
        }
        // packet.up();
    }
};

const ChatRoom = {
    applyOverride(chatRoom, conference) {
        /**
         *
         * @param pres
         */
        chatRoom.onPresence = function(pres) {
            const from = pres.getAttribute('from');
            const member = {};
            const statusEl = pres.getElementsByTagName('status')[0];

            if (statusEl) {
                member.status = statusEl.textContent || '';
            }
            let hasStatusUpdate = false;
            let hasVersionUpdate = false;
            const xElement
                = pres.getElementsByTagNameNS(
                'http://jabber.org/protocol/muc#user', 'x')[0];
            const mucUserItem
                = xElement && xElement.getElementsByTagName('item')[0];

            member.isReplaceParticipant
                = pres.getElementsByTagName('flip_device').length;

            member.affiliation
                = mucUserItem && mucUserItem.getAttribute('affiliation');
            member.role = mucUserItem && mucUserItem.getAttribute('role');

            // Focus recognition
            const jid = mucUserItem && mucUserItem.getAttribute('jid');

            member.jid = jid;
            member.isFocus = this.xmpp.moderator.isFocusJid(jid);
            member.isHiddenDomain
                = jid && jid.indexOf('@') > 0
                && (this.options.hiddenDomain
                === jid.substring(jid.indexOf('@') + 1, jid.indexOf('/'))
                    || jid.split('@')[0] === 'admin');

            const id = Strophe.getResourceFromJid(from);

            kickUserIfSameJidAndDifferentResource(pres, jid, id, member, chatRoom);

            this.eventEmitter.emit(XMPPEvents.PRESENCE_RECEIVED, {
                fromHiddenDomain: member.isHiddenDomain,
                presence: pres
            });

            const xEl = pres.querySelector('x');

            if (xEl) {
                xEl.remove();
            }

            const nodes = [];

            parser.packet2JSON(pres, nodes);
            this.lastPresences[from] = nodes;

            // process nodes to extract data needed for MUC_JOINED and
            // MUC_MEMBER_JOINED events
            const extractIdentityInformation = node => {
                const identity = {};
                const userInfo = node.children.find(c => c.tagName === 'user');

                if (userInfo) {
                    identity.user = {};
                    const tags = [ 'id', 'name', 'avatar-url' ];

                    if (this.options.hiddenFromRecorderFeatureEnabled) {
                        tags.push('hidden-from-recorder');
                    }

                    for (const tag of tags) {
                        const child
                            = userInfo.children.find(c => c.tagName === tag);

                        if (child) {
                            identity.user[tag] = child.value;
                        }
                    }
                }
                const groupInfo = node.children.find(c => c.tagName === 'group');

                if (groupInfo) {
                    identity.group = groupInfo.value;
                }

                return identity;
            };

            for (let i = 0; i < nodes.length; i++) {
                const node = nodes[i];

                switch (node.tagName) {
                case 'bot': {
                    const { attributes } = node;

                    if (!attributes) {
                        break;
                    }
                    const { type } = attributes;

                    member.botType = type;
                    break;
                }
                case 'nick':
                    member.nick = node.value;

                    // Hide transcriber in the room
                    if (jid.split('@')[0] === 'jigasi' && member.nick?.toLowerCase() === 'transcriber') {
                        member.isHiddenDomain = true;
                    }
                    break;
                case 'displayName':
                    member.displayName = node.value;
                    break;
                case 'silent':
                    member.isSilent = node.value;
                    break;
                case 'userId':
                    member.id = node.value;
                    break;
                case 'stats-id':
                    member.statsID = node.value;
                    break;
                case 'identity':
                    member.identity = extractIdentityInformation(node);
                    break;
                case 'ctijid':
                    member.ctijid = node.value;
                    break;
                case 'features': {
                    member.features = this._extractFeatures(node);
                    break;
                }
                case 'stat': {
                    const { attributes } = node;

                    if (!attributes) {
                        break;
                    }
                    const { name } = attributes;

                    if (name === 'version') {
                        member.version = attributes.value;
                    }
                    break;
                }
                }
            }

            if (!this.joined && !this.inProgressEmitted) {
                const now = this.connectionTimes['muc.join.started'] = window.performance.now();

                logger.log('(TIME) MUC join started:\t', now);

                this.eventEmitter.emit(XMPPEvents.MUC_JOIN_IN_PROGRESS);
                this.inProgressEmitted = true;
            }

            if (from === this.myroomjid) {
                const newRole
                    = member.affiliation === 'owner' ? member.role : 'none';

                if (this.role !== newRole) {
                    this.role = newRole;
                    this.eventEmitter.emit(
                        XMPPEvents.LOCAL_ROLE_CHANGED,
                        this.role);
                }
                if (!this.joined) {
                    this.joined = true;
                    const now = this.connectionTimes['muc.joined']
                        = window.performance.now();

                    logger.log('(TIME) MUC joined:\t', now);

                    // set correct initial state of locked
                    if (this.password) {
                        this.locked = true;
                    }

                    // Re-send presence in case any presence updates were added,
                    // but blocked from sending, during the join process.
                    // send the presence only if there was a modification after we had synced it
                    if (this.presenceUpdateTime >= this.presenceSyncTime) {
                        this.sendPresence();
                    }

                    // we need to reset it because of breakout rooms which will reuse connection but will invite jicofo
                    this.xmpp.moderator.conferenceRequestSent = false;
                    this.eventEmitter.emit(XMPPEvents.MUC_JOINED);

                    // Now let's check the disco-info to retrieve the
                    // meeting Id if any
                    !this.options.disableDiscoInfo && this.discoRoomInfo();
                }
            } else if (jid === undefined) {
                logger.info('Ignoring member with undefined JID');
            } else if (this.members[from] === undefined) {
                // new participant
                this.members[from] = member;
                logger.log('entered', from, member);
                hasStatusUpdate = member.status !== undefined;
                hasVersionUpdate = member.version !== undefined;
                if (member.isFocus) {
                    this._initFocus(from, member.features);
                } else {
                    // identity is being added to member joined, so external
                    // services can be notified for that (currently identity is
                    // not used inside library)
                    this.eventEmitter.emit(
                        WildixXMPPEvents.MUC_MEMBER_JOINED,
                        from,
                        member.nick || member.displayName,
                        member.role,
                        member.isHiddenDomain,
                        member.statsID,
                        member.status,
                        member.identity,
                        member.botType,
                        member.jid,
                        member.features,
                        member.isReplaceParticipant,
                        member.isSilent,
                        member.ctijid);

                    // we are reporting the status with the join
                    // so we do not want a second event about status update
                    hasStatusUpdate = false;
                }
            } else {
                // Presence update for existing participant
                // Watch role change:
                const memberOfThis = this.members[from];

                if (memberOfThis.role !== member.role) {
                    memberOfThis.role = member.role;
                    this.eventEmitter.emit(
                        XMPPEvents.MUC_ROLE_CHANGED, from, member.role);
                }

                // affiliation changed
                if (memberOfThis.affiliation !== member.affiliation) {
                    memberOfThis.affiliation = member.affiliation;
                }

                // fire event that botType had changed
                if (memberOfThis.botType !== member.botType) {
                    memberOfThis.botType = member.botType;
                    this.eventEmitter.emit(
                        XMPPEvents.MUC_MEMBER_BOT_TYPE_CHANGED,
                        from,
                        member.botType);
                }

                if (member.isFocus) {
                    // From time to time first few presences of the focus are not
                    // containing it's jid. That way we can mark later the focus
                    // member instead of not marking it at all and not starting the
                    // conference.
                    // FIXME: Maybe there is a better way to handle this issue. It
                    // seems there is some period of time in prosody that the
                    // configuration form is received but not applied. And if any
                    // participant joins during that period of time the first
                    // presence from the focus won't contain
                    // <item jid="focus..." />.
                    // By default we are disabling the waiting for form submission in order to use the room
                    // and we had enabled by default that jids are public in the room ,
                    // so this case should not happen, if public jid is turned off we will receive the jid
                    // when we become moderator in the room
                    memberOfThis.isFocus = true;
                    this._initFocus(from, member.features);
                }

                // store the new display name
                if (member.displayName) {
                    memberOfThis.displayName = member.displayName;
                }

                // join without audio
                if (member.isSilent) {
                    memberOfThis.isSilent = member.isSilent;
                }

                // update stored status message to be able to detect changes
                if (memberOfThis.status !== member.status) {
                    hasStatusUpdate = true;
                    memberOfThis.status = member.status;
                }

                if (memberOfThis.version !== member.version) {
                    hasVersionUpdate = true;
                    memberOfThis.version = member.version;
                }

                if (!isEqual(memberOfThis.features, member.features)) {
                    memberOfThis.features = member.features;
                    this.eventEmitter.emit(XMPPEvents.PARTICIPANT_FEATURES_CHANGED, from, member.features);
                }
            }

            const participantProperties = new Map();

            // after we had fired member or room joined events, lets fire events
            // for the rest info we got in presence
            for (let i = 0; i < nodes.length; i++) {
                const node = nodes[i];

                switch (node.tagName) {
                case 'nick':
                    if (!member.isFocus) {
                        const displayName
                        = this.xmpp.options.displayJids
                            ? Strophe.getResourceFromJid(from)
                            : member.nick;

                        this.eventEmitter.emit(
                            XMPPEvents.DISPLAY_NAME_CHANGED,
                            from,
                            displayName);
                    }
                    break;
                case 'silent':
                    this.eventEmitter.emit(
                        XMPPEvents.SILENT_STATUS_CHANGED,
                        from,
                        member.isSilent);
                    break;
                case 'bridgeNotAvailable':
                    if (member.isFocus && !this.noBridgeAvailable) {
                        this.noBridgeAvailable = true;
                        this.eventEmitter.emit(XMPPEvents.BRIDGE_DOWN);
                    }
                    break;
                case `jitsi_participant_${WildixXMPPEvents.CONTROL_SHARING_DATA}`:
                    this.eventEmitter.emit(WildixXMPPEvents.CONTROL_SHARING_DATA, node.value);
                    break;
                case 'jitsi_participant_transcription_language':
                    setTimeout(() => {
                        this.eventEmitter.emit(WildixXMPPEvents.TRANSCRIPTION_LANGUAGE, node.value, id);
                    }, 300);

                    break;
                case 'conference-properties':
                    if (member.isFocus) {
                        const properties = {};

                        for (let j = 0; j < node.children.length; j++) {
                            const {attributes} = node.children[j];

                            if (attributes && attributes.key) {
                                properties[attributes.key] = attributes.value;
                            }
                        }

                        this.eventEmitter.emit(XMPPEvents.CONFERENCE_PROPERTIES_CHANGED, properties);
                    }
                    break;
                case 'transcription-status': {
                    const {attributes} = node;

                    if (!attributes) {
                        break;
                    }

                    const {status} = attributes;

                    if (status && status !== this.transcriptionStatus
                        && member.isHiddenDomain && member.features?.has(FEATURE_TRANSCRIBER)) {
                        this.transcriptionStatus = status;
                        this.eventEmitter.emit(
                            XMPPEvents.TRANSCRIPTION_STATUS_CHANGED,
                            status,
                            Strophe.getResourceFromJid(from)
                        );
                        this.eventEmitter.emit(WildixXMPPEvents.TRANSCRIPTION_LANGUAGE_STATUS, status);
                    }

                    break;
                }
                case 'call-control': {
                    const att = node.attributes;

                    if (!att) {
                        break;
                    }
                    this.phoneNumber = att.phone || null;
                    this.phonePin = att.pin || null;
                    this.eventEmitter.emit(XMPPEvents.PHONE_NUMBER_CHANGED);
                    break;
                }
                default: {
                    if (node.tagName.startsWith('jitsi_participant_')) {
                        participantProperties
                            .set(node.tagName.substring('jitsi_participant_'.length), node.value);
                    } else {
                        this.processNode(node, from);
                    }
                }
                }
            }

            // All participant properties are in `participantProperties`, call the event handlers now.
            const participantId = Strophe.getResourceFromJid(from);

            for (const [ key, value ] of participantProperties) {
                this.participantPropertyListener(participantId, key, value);
            }

            // Trigger status message update if necessary
            if (hasStatusUpdate) {
                this.eventEmitter.emit(
                    XMPPEvents.PRESENCE_STATUS,
                    from,
                    member.status);
            }

            if (hasVersionUpdate) {
                logger.info(`Received version for ${jid}: ${member.version}`);
            }
        };

        /**
         *
         * @param pres
         * @param from
         */
        chatRoom.onPresenceUnavailable = function(pres, from) {
            // ignore presence
            if ($(pres).find('>ignore[xmlns="http://jitsi.org/jitmeet/"]').length) {
                return true;
            }

            // room destroyed ?
            const destroySelect = $(pres).find('>x[xmlns="http://jabber.org/protocol/muc#user"]>destroy');

            if (destroySelect.length) {
                let reason;
                const reasonSelect
                    = $(pres).find(
                    '>x[xmlns="http://jabber.org/protocol/muc#user"]'
                    + '>destroy>reason');

                if (reasonSelect.length) {
                    reason = reasonSelect.text();
                }

                this.eventEmitter.emit(XMPPEvents.MUC_DESTROYED, reason, destroySelect.attr('jid'));
                this.connection.emuc.doLeave(this.roomjid);

                return true;
            }

            // Status code 110 indicates that this notification is "self-presence".
            const isSelfPresence
                = $(pres)
                .find(
                    '>x[xmlns="http://jabber.org/protocol/muc#user"]>'
                    + 'status[code="110"]')
                .length;
            const isKick
                = $(pres)
                .find(
                    '>x[xmlns="http://jabber.org/protocol/muc#user"]'
                    + '>status[code="307"]')
                .length;
            const membersKeys = Object.keys(this.members);
            const isReplaceParticipant = $(pres).find('flip_device').length;

            if (isKick) {
                const actorSelect
                    = $(pres)
                    .find('>x[xmlns="http://jabber.org/protocol/muc#user"]>item>actor');
                let actorNick;

                if (actorSelect.length) {
                    actorNick = actorSelect.attr('nick');
                }

                let reason;
                const reasonSelect
                    = $(pres).find(
                    '>x[xmlns="http://jabber.org/protocol/muc#user"]'
                    + '>item>reason');

                if (reasonSelect.length) {
                    reason = reasonSelect.text();
                }

                // we first fire the kicked so we can show the participant
                // who kicked, before notifying that participant left
                // we fire kicked for us and for any participant kicked
                this.eventEmitter.emit(
                    XMPPEvents.KICKED,
                    isSelfPresence,
                    actorNick,
                    Strophe.getResourceFromJid(from),
                    reason,
                    isReplaceParticipant);
            }

            if (isSelfPresence) {
                // If the status code is 110 this means we're leaving and we would
                // like to remove everyone else from our view, so we trigger the
                // event.
                membersKeys.forEach(jid => {
                    const member = this.members[jid];

                    delete this.members[jid];
                    delete this.lastPresences[jid];
                    if (!member.isFocus) {
                        this.eventEmitter.emit(XMPPEvents.MUC_MEMBER_LEFT, jid);
                    }
                });
                this.connection.emuc.doLeave(this.roomjid);

                // we fire muc_left only if this is not a kick,
                // kick has both statuses 110 and 307.
                if (!isKick) {
                    this.eventEmitter.emit(XMPPEvents.MUC_LEFT);
                }
            } else {
                const reasonSelect = $(pres).find('>status');
                const member = this.members[from];
                let reason;

                if (reasonSelect.length) {
                    reason = reasonSelect.text();
                }

                delete this.members[from];
                delete this.lastPresences[from];

                // In this case we *do* fire MUC_MEMBER_LEFT for the focus?
                this.eventEmitter.emit(XMPPEvents.MUC_MEMBER_LEFT, from, reason);

                if (member && member.isHiddenDomain && member.features?.has(FEATURE_TRANSCRIBER)
                    && this.transcriptionStatus !== JitsiTranscriptionStatus.OFF) {
                    this.transcriptionStatus = JitsiTranscriptionStatus.OFF;
                    this.eventEmitter.emit(
                        XMPPEvents.TRANSCRIPTION_STATUS_CHANGED,
                        this.transcriptionStatus,
                        Strophe.getResourceFromJid(from),
                        true /* exited abruptly */
                    );
                }

                if (member?.isFocus) {
                    logger.info('Focus has left the room - leaving conference');
                    this.eventEmitter.emit(XMPPEvents.FOCUS_LEFT);
                }
            }
        };

        chatRoom.onMessage = function(msg, from) {
            const type = msg.getAttribute('type');

            if (type === 'error') {
                const settingsErrorMsg = $(msg).find('>settings-error>text').text();

                if (settingsErrorMsg.length) {
                    this.eventEmitter.emit(XMPPEvents.SETTINGS_ERROR_RECEIVED, settingsErrorMsg);

                    return true;
                }
                const errorMsg = $(msg).find('>error>text').text();

                this.eventEmitter.emit(XMPPEvents.CHAT_ERROR_RECEIVED, errorMsg);

                return true;
            }

            const reactions = $(msg).find('>[xmlns="urn:xmpp:reactions:0"]>reaction');

            if (reactions.length > 0) {
                const messageId = $(msg).find('>[xmlns="urn:xmpp:reactions:0"]').attr('id');
                const reactionList = [];

                reactions.each((_, reactionElem) => {
                    const reaction = $(reactionElem).text();

                    reactionList.push(reaction);
                });

                this.eventEmitter.emit(XMPPEvents.REACTION_RECEIVED, from, reactionList, messageId);

                return true;
            }


            const txt = $(msg).find('>body').text();
            const subject = $(msg).find('>subject');
            const profile = {};

            const profileEl = $(msg).find('profile');

            if (profileEl && profileEl.length) {
                profile.displayName = profileEl.find('displayname').text();
                profile.ctijid = profileEl.find('ctijid').text();
                profile.userId = profileEl.find('userid').text();
            }

            if (subject.length) {
                const subjectText = subject.text();

                if (subjectText || subjectText === '') {
                    this.eventEmitter.emit(XMPPEvents.SUBJECT_CHANGED, subjectText);
                    logger.log(`Subject is changed to ${subjectText}`);
                }
            }

            // xep-0203 delay
            let stamp = $(msg).find('>delay').attr('stamp');

            if (!stamp) {
                // or xep-0091 delay, UTC timestamp
                stamp = $(msg).find('>[xmlns="jabber:x:delay"]').attr('stamp');

                if (stamp) {
                    // the format is CCYYMMDDThh:mm:ss
                    const dateParts
                        = stamp.match(/(\d{4})(\d{2})(\d{2}T\d{2}:\d{2}:\d{2})/);

                    stamp = `${dateParts[1]}-${dateParts[2]}-${dateParts[3]}Z`;
                }
            }

            if (from === this.roomjid) {
                let invite;

                if ($(msg).find('>x[xmlns="http://jabber.org/protocol/muc#user"]>status[code="104"]').length) {
                    this.discoRoomInfo();
                } else if ((invite = $(msg).find('>x[xmlns="http://jabber.org/protocol/muc#user"]>invite'))
                    && invite.length) {
                    const passwordSelect = $(msg).find('>x[xmlns="http://jabber.org/protocol/muc#user"]>password');
                    let password;

                    if (passwordSelect && passwordSelect.length) {
                        password = passwordSelect.text();
                    }

                    this.eventEmitter.emit(XMPPEvents.INVITE_MESSAGE_RECEIVED,
                        from, invite.attr('from'), txt, password);
                }
            }

            const jsonMessage = $(msg).find('>json-message').text();

            if (jsonMessage) {
                const parsedJson = this.xmpp.tryParseJSONAndVerify(jsonMessage);

                // We emit this event if the message is a valid json, and is not
                // delivered after a delay, i.e. stamp is undefined.
                // e.g. - subtitles should not be displayed if delayed.
                if (parsedJson && stamp === undefined) {
                    this.eventEmitter.emit(XMPPEvents.JSON_MESSAGE_RECEIVED,
                        from, parsedJson);

                    return;
                }
            }

            if (txt) {
                const id = Strophe.getResourceFromJid(from);
                const messageId = $(msg).attr('id') || uuidv4();

                if (type === 'chat') {
                    conference.eventEmitter.emit(
                        JitsiConferenceEvents.PRIVATE_MESSAGE_RECEIVED, id, txt, stamp, messageId);
                } else if (type === 'groupchat') {
                    const nickEl = $(msg).find('>nick');
                    let nick;
                    let isGuest = false;

                    if (nickEl.length > 0) {
                        nick = nickEl.text();
                        isGuest = true;
                    } else {
                        nick = profile.displayName;
                    }

                    // we will fire explicitly that this is a guest(isGuest:true) to the conference
                    // informing that this is probably a message from a guest to the conference (visitor)
                    // a message with explicit name set
                    conference.eventEmitter.emit(
                        JitsiConferenceEvents.MESSAGE_RECEIVED, id, txt, stamp, nick, isGuest, messageId);
                }

                if (navigator.product !== 'ReactNative') {
                    if (APP.conference.getMyUserId() !== profile.userId && !stamp && txt.trim() !== '') {
                        APP.wildix.eventEmitter.emit(WildixEvents.CHAT_RECEIVER, {
                            name: profile.displayName,
                            title: profile.displayName,
                            body: txt
                        });
                    }
                }
            }
        };


        chatRoom.sendMessage = function(message, elementName, profile) {
            // eslint-disable-next-line no-undef
            const msg = $msg({ to: this.roomjid,
                type: 'groupchat' });

            // We are adding the message in a packet extension. If this element
            // is different from 'body', we add a custom namespace.
            // e.g. for 'json-message' extension of message stanza.
            if (elementName === 'body') {
                msg.c(elementName, {}, message);
                msg.c('profile', { xmlns: 'urn:wildix:profile' });
                msg.c('displayname', profile.displayName).up();
                msg.c('ctijid', profile.ctijid).up();
                msg.c('userid', profile.userId).up();
                msg.up();
            } else {
                msg.c(elementName, { xmlns: 'http://jitsi.org/jitmeet' }, message);
            }

            this.connection.send(msg);
            this.eventEmitter.emit(XMPPEvents.SENDING_CHAT_MESSAGE, message);
        };

        chatRoom.sendPrivateMessage = function(id, message, elementName, profile) {
            // eslint-disable-next-line no-undef
            const msg = $msg({ to: `${this.roomjid}/${id}`,
                type: 'chat' });

            if (elementName === 'body' || elementName === 'json-message') {
                msg.c(elementName, message).up();
            } else {
                msg.c(elementName, { xmlns: 'http://jitsi.org/jitmeet' }, message)
                    .up();
            }

            if (elementName === 'body') {
                // do not store in mam (fix private messages)
                msg.c('no-store', { xmlns: 'urn:xmpp:hints' }).up();

                // add profile node with user information
                msg.c('profile', { xmlns: 'urn:wildix:profile' });
                msg.c('displayname', profile.displayName).up();
                msg.c('ctijid', profile.ctijid).up();
                msg.c('userid', profile.userId).up();
                msg.up();
            } else if (elementName === 'json-message') {
                // do not store in mam (fix private messages)
                msg.c('no-store', { xmlns: 'urn:xmpp:hints' }).up();

            }

            this.connection.send(msg);
            this.eventEmitter.emit(
                XMPPEvents.SENDING_PRIVATE_CHAT_MESSAGE, message);
        };

        chatRoom.getMembers = function() {
            return this.members;
        };
    }
};


export default ChatRoom;
